/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */

import {APIService} from '@/core/util/services/api.service';

type AdjustmentAction = {
  component: string;
  props: object;
};

type AdjustmentSecondaryAction = {
  label: string;
  context: string;
};

type useAdjustmentArgs = {
  primaryActions?: {[name: string]: AdjustmentAction};
  secondaryActions?: AdjustmentSecondaryAction[];
};

const approve: AdjustmentAction = {
  component: 'oxd-button',
  props: {
    label: 'Approve',
    displayType: 'label-success',
    size: 'medium',
    onClick: null,
  },
};

const reject: AdjustmentAction = {
  component: 'oxd-button',
  props: {
    label: 'Reject',
    displayType: 'label-danger',
    size: 'medium',
    onClick: null,
  },
};

export const ADJUSTMENT_APPROVAL_OVERLAP_PREFIX =
  'ADJUSTMENT_APPROVAL_OVERLAP:';

export const parseAdjustmentApprovalOverlapRecordId = (
  message?: string | null,
): number | null => {
  if (!message?.startsWith(ADJUSTMENT_APPROVAL_OVERLAP_PREFIX)) {
    return null;
  }
  const id = Number(message.slice(ADJUSTMENT_APPROVAL_OVERLAP_PREFIX.length));
  return Number.isNaN(id) || id <= 0 ? null : id;
};

export type AdjustmentActionError = Error & {
  apiMessage?: string | null;
  overlapRecordId?: number | null;
};

const extractApiErrorMessage = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed) as {error?: {message?: string}};
        return parsed?.error?.message ?? trimmed;
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as {
    apiMessage?: string | null;
    message?: string;
    error?: {message?: string};
    data?: {error?: {message?: string}};
    response?: {data?: {error?: {message?: string}}};
  };

  return (
    extractApiErrorMessage(candidate.apiMessage) ??
    extractApiErrorMessage(candidate.error?.message) ??
    extractApiErrorMessage(candidate.data?.error?.message) ??
    extractApiErrorMessage(candidate.response?.data?.error?.message) ??
    extractApiErrorMessage(candidate.message)
  );
};

export const getAdjustmentActionErrorMessage = (
  error: unknown,
): string | null => {
  return extractApiErrorMessage(error);
};

export default function useAdjustmentActions(
  http: APIService,
  {primaryActions = {approve, reject}}: useAdjustmentArgs = {},
) {
  const processAdjustmentAction = (
    id: number,
    statusId: number,
    employeeAttendanceId?: number | null,
  ) => {
    const payload: {status: number; employeeAttendanceId?: number} = {
      status: statusId,
    };
    if (employeeAttendanceId) {
      payload.employeeAttendanceId = employeeAttendanceId;
    }

    return http.update(id, payload);
  };

  return {
    adjustmentActions: primaryActions,
    processAdjustmentAction,
  };
}
